```vue
<template>
  <div class="jim-scheduling-container">
    <div class="report-page">
      <h1 class="page-heading">WIP Inventory Value Report</h1>

      <div class="report-layout">
        <div class="left-side-section">
          <button
            class="plan-it-btn"
            @click="loadReport"
            :disabled="loading"
          >
            {{ loading ? "Loading…" : "Refresh" }}
          </button>

          <button
            class="plan-it-btn"
            @click="printReport"
            :disabled="!reportLoaded"
          >
            Print
          </button>

          <button
            class="plan-it-btn"
            @click="exportCSV"
            :disabled="!reportLoaded"
          >
            Export CSV
          </button>

          <div class="side-help">
            <div><strong>Cost:</strong> Macola standard cost</div>
            <div><strong>WIP:</strong> mat_cost_type = W</div>
            <div><strong>Type:</strong> First 4 characters of item number</div>
            <div><strong>Locations:</strong> F, D, K, L</div>
            <div><strong>Virtual WHs:</strong> WF, WD, WK, WL rolled up</div>
          </div>
        </div>

        <div class="report-wrapper">
          <div class="summary-bar">
            <div class="summary-item">
              <div class="summary-label">F Value</div>
              <div class="summary-value">
                {{ formatCurrency(locationTotals.F.value) }}
              </div>
              <div class="summary-detail">
                {{ formatQuantity(locationTotals.F.qty) }} units
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">D Value</div>
              <div class="summary-value">
                {{ formatCurrency(locationTotals.D.value) }}
              </div>
              <div class="summary-detail">
                {{ formatQuantity(locationTotals.D.qty) }} units
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">K Value</div>
              <div class="summary-value">
                {{ formatCurrency(locationTotals.K.value) }}
              </div>
              <div class="summary-detail">
                {{ formatQuantity(locationTotals.K.qty) }} units
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">L Value</div>
              <div class="summary-value">
                {{ formatCurrency(locationTotals.L.value) }}
              </div>
              <div class="summary-detail">
                {{ formatQuantity(locationTotals.L.qty) }} units
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">WIP Items</div>
              <div class="summary-value">
                {{ formatNumber(totals.item_count) }}
              </div>
              <div class="summary-detail">
                {{ formatNumber(typeSummary.length) }} types
              </div>
            </div>

            <div class="summary-item total-card">
              <div class="summary-label">Total WIP Value</div>
              <div class="summary-value">
                {{ formatCurrency(totals.total_value) }}
              </div>
              <div class="summary-detail">
                {{ formatQuantity(totals.total_qty) }} total units
              </div>
            </div>
          </div>

          <div class="table-panel">
            <div class="table-header-row">
              <div class="small-muted" v-if="loading">
                Loading WIP inventory values…
              </div>

              <div class="small-muted" v-else>
                Inventory value equals location quantity on hand multiplied by
                that location's Macola standard cost. Type is based on the first
                four characters of the item number.
              </div>
            </div>

            <div class="table-scroll">
              <table class="report-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Item No</th>
                    <th>Description</th>
                    <th>Cost Type</th>
                    <th>Avg Std Cost</th>

                    <th>F Qty</th>
                    <th>F Value</th>

                    <th>D Qty</th>
                    <th>D Value</th>

                    <th>K Qty</th>
                    <th>K Value</th>

                    <th>L Qty</th>
                    <th>L Value</th>

                    <th>Total Qty</th>
                    <th>Total Value</th>
                  </tr>
                </thead>

                <tbody>
                  <template
                    v-for="group in groupedRows"
                    :key="group.type"
                  >
                    <tr class="type-heading-row">
                      <td colspan="15">
                        {{ group.type }}

                        <span class="type-heading-summary">
                          {{ formatNumber(group.rows.length) }} items
                          ·
                          {{ formatCurrency(
                            group.rows.reduce(
                              (sum, row) => sum + Number(row.total_value || 0),
                              0
                            )
                          ) }}
                        </span>
                      </td>
                    </tr>

                    <tr
                      v-for="row in group.rows"
                      :key="row.item_no"
                    >
                      <td class="type-cell">
                        {{ row.type }}
                      </td>

                      <td class="item-cell">
                        {{ row.item_no }}
                      </td>

                      <td class="description-cell">
                        {{ row.description }}
                      </td>

                      <td>
                        {{ row.mat_cost_type }}
                      </td>

                      <td>
                        {{ formatCurrency(row.avg_std_cost, 4) }}
                      </td>

                      <td>
                        {{ formatQuantity(row.F_qty) }}
                      </td>

                      <td>
                        {{ formatCurrency(row.F_value) }}
                      </td>

                      <td>
                        {{ formatQuantity(row.D_qty) }}
                      </td>

                      <td>
                        {{ formatCurrency(row.D_value) }}
                      </td>

                      <td>
                        {{ formatQuantity(row.K_qty) }}
                      </td>

                      <td>
                        {{ formatCurrency(row.K_value) }}
                      </td>

                      <td>
                        {{ formatQuantity(row.L_qty) }}
                      </td>

                      <td>
                        {{ formatCurrency(row.L_value) }}
                      </td>

                      <td class="total-qty-cell">
                        {{ formatQuantity(row.total_qty) }}
                      </td>

                      <td class="total-value-cell">
                        {{ formatCurrency(row.total_value) }}
                      </td>
                    </tr>
                  </template>

                  <tr class="total-row">
                    <td colspan="5">
                      Total
                    </td>

                    <td>
                      {{ formatQuantity(locationTotals.F.qty) }}
                    </td>

                    <td>
                      {{ formatCurrency(locationTotals.F.value) }}
                    </td>

                    <td>
                      {{ formatQuantity(locationTotals.D.qty) }}
                    </td>

                    <td>
                      {{ formatCurrency(locationTotals.D.value) }}
                    </td>

                    <td>
                      {{ formatQuantity(locationTotals.K.qty) }}
                    </td>

                    <td>
                      {{ formatCurrency(locationTotals.K.value) }}
                    </td>

                    <td>
                      {{ formatQuantity(locationTotals.L.qty) }}
                    </td>

                    <td>
                      {{ formatCurrency(locationTotals.L.value) }}
                    </td>

                    <td>
                      {{ formatQuantity(locationTotals.Total.qty) }}
                    </td>

                    <td class="total-value-cell">
                      {{ formatCurrency(locationTotals.Total.value) }}
                    </td>
                  </tr>

                  <tr v-if="!loading && rows.length === 0">
                    <td colspan="15" class="empty-row">
                      No WIP inventory was found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            v-if="!loading && typeSummary.length"
            class="table-panel type-summary-panel"
          >
            <div class="table-header-row">
              <div class="small-muted">
                WIP inventory totals by four-character item type.
              </div>
            </div>

            <div class="table-scroll small-scroll">
              <table class="report-table type-summary-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Item Count</th>
                    <th>Total Quantity</th>
                    <th>Total Value</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="typeRow in typeSummary"
                    :key="typeRow.type"
                  >
                    <td class="type-cell">
                      {{ typeRow.type }}
                    </td>

                    <td>
                      {{ formatNumber(typeRow.item_count) }}
                    </td>

                    <td>
                      {{ formatQuantity(typeRow.total_qty) }}
                    </td>

                    <td class="total-value-cell">
                      {{ formatCurrency(typeRow.total_value) }}
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>Total</td>

                    <td>
                      {{ formatNumber(totals.item_count) }}
                    </td>

                    <td>
                      {{ formatQuantity(totals.total_qty) }}
                    </td>

                    <td class="total-value-cell">
                      {{ formatCurrency(totals.total_value) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PabloChatSidebar :loc="selectedLocation" />
    </div>
  </div>
</template>


<script>
import axios from "axios";
import PabloChatSidebar from "./PabloChatSidebar.vue";


export default {
  name: "WIPInventoryValueReport",

  components: {
    PabloChatSidebar
  },

  data() {
    return {
        loading: false,
        rows: [],
        typeSummary: [],

        totals: {
        item_count: 0,
        total_qty: 0,
        total_value: 0
        },

        locationTotals: {
        F: { qty: 0, value: 0 },
        D: { qty: 0, value: 0 },
        K: { qty: 0, value: 0 },
        L: { qty: 0, value: 0 },
        Total: { qty: 0, value: 0 }
        }
    };
  },

  computed: {
    selectedLocation() {
      return this.$root.selectedLocation;
    },

    reportLoaded() {
      return Array.isArray(this.rows) && this.rows.length > 0;
    },

    groupedRows() {
        const groups = new Map();

        for (const row of this.rows) {
            const type = String(row.type || "OTHER");

            if (!groups.has(type)) {
            groups.set(type, []);
            }

            groups.get(type).push(row);
        }

        return Array.from(groups.entries())
            .map(([type, rows]) => ({
            type,
            rows
            }))
            .sort((a, b) =>
            String(a.type).localeCompare(String(b.type))
            );
    }
  },

  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-US", {
        maximumFractionDigits: 0
      });
    },

    formatQuantity(value) {
      return Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    formatCurrency(value, decimals = 2) {
      return Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    },

    async loadReport() {
        this.loading = true;

        try {
            const { data } = await axios.get(
            "/wip-inventory-value-report",
            {
                params: {
                location: this.selectedLocation
                }
            }
            );

            this.rows = Array.isArray(data?.rows)
            ? data.rows
            : [];

            this.typeSummary = Array.isArray(data?.typeSummary)
            ? data.typeSummary
            : [];

            this.totals = data?.totals || {
            item_count: 0,
            total_qty: 0,
            total_value: 0
            };

            this.locationTotals = data?.locationTotals || {
            F: { qty: 0, value: 0 },
            D: { qty: 0, value: 0 },
            K: { qty: 0, value: 0 },
            L: { qty: 0, value: 0 },
            Total: { qty: 0, value: 0 }
            };
        } catch (err) {
            console.error(
            "load WIP inventory value report error:",
            err
            );

            alert("Failed to load WIP inventory value report.");
        } finally {
            this.loading = false;
        }
    },

    printReport() {
      if (!this.reportLoaded) return;

      const payload = {
        meta: {
          generatedAt: new Date().toISOString(),
          units: "US dollars",
          source:
            "Macola iminvloc std_cost and imitmidx mat_cost_type",
          virtualWarehouses:
            "WF→F, WD→D, WK→K, WL→L"
        },
        rows: this.rows,
        summary: this.summary,
        locationTotals: this.locationTotals
      };

      sessionStorage.setItem(
        "pablo_wip_inventory_value_print_payload",
        JSON.stringify(payload)
      );

      const routeData = this.$router.resolve({
        path: "/print-wip-inventory-value-report"
      });

      window.open(routeData.href, "_blank");
    },

    exportCSV() {
        if (!this.reportLoaded) return;

        const csvRows = [];

        csvRows.push([
            "Type",
            "Item No",
            "Description",
            "Material Cost Type",
            "Average Standard Cost",
            "F Quantity",
            "F Value",
            "D Quantity",
            "D Value",
            "K Quantity",
            "K Value",
            "L Quantity",
            "L Value",
            "Total Quantity",
            "Total Value"
        ]);

        for (const row of this.rows) {
            csvRows.push([
            row.type,
            row.item_no,
            row.description,
            row.mat_cost_type,
            Number(row.avg_std_cost || 0),

            Number(row.F_qty || 0),
            Number(row.F_value || 0),

            Number(row.D_qty || 0),
            Number(row.D_value || 0),

            Number(row.K_qty || 0),
            Number(row.K_value || 0),

            Number(row.L_qty || 0),
            Number(row.L_value || 0),

            Number(row.total_qty || 0),
            Number(row.total_value || 0)
            ]);
        }

        csvRows.push([
            "TOTAL",
            "",
            "",
            "",
            "",

            Number(this.locationTotals.F.qty || 0),
            Number(this.locationTotals.F.value || 0),

            Number(this.locationTotals.D.qty || 0),
            Number(this.locationTotals.D.value || 0),

            Number(this.locationTotals.K.qty || 0),
            Number(this.locationTotals.K.value || 0),

            Number(this.locationTotals.L.qty || 0),
            Number(this.locationTotals.L.value || 0),

            Number(this.locationTotals.Total.qty || 0),
            Number(this.locationTotals.Total.value || 0)
        ]);

        const csv = csvRows
            .map((row) =>
            row
                .map((value) => {
                const escaped = String(value ?? "").replace(/"/g, '""');
                return `"${escaped}"`;
                })
                .join(",")
            )
            .join("\n");

        const blob = new Blob(["\uFEFF" + csv], {
            type: "text/csv;charset=utf-8;"
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const today = new Date().toISOString().slice(0, 10);

        link.href = url;
        link.setAttribute(
            "download",
            `wip_inventory_value_${today}.csv`
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
  },

  mounted() {
    this.loadReport();
  }
};
</script>

<style scoped>
.jim-scheduling-container {
  font-family: Garamond, serif;
  color: #cbbfbf;
  margin-top: 20px;
  background-color: #0b0101;
  min-height: 100vh;
  padding-bottom: 40px;
  text-align: left !important;
}

.page-heading {
  font-family: "Cormorant Garamond", serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

.report-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.report-layout {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  color: #fff;
  margin-top: 80px;
}

.report-wrapper {
  width: 100%;
  min-width: 0;
}

.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 125px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin-bottom: 12px;
  background: rgb(67, 67, 67);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 150px;
  flex: 1 1 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 17px;
  opacity: 0.8;
}

.summary-value {
  font-size: 22px;
  font-weight: bold;
  color: #cbbfbf;
  margin-top: 3px;
}

.summary-detail {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 3px;
}

.total-card .summary-value {
  color: #f5d76e;
}

.table-panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}

.table-header-row {
  margin-bottom: 8px;
}

.table-scroll {
  max-height: 78vh;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #333;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1900px;
}

.report-table th,
.report-table td {
  border: 1px solid #333;
  padding: 8px 10px;
  font-size: 14px;
  text-align: right;
  white-space: nowrap;
}

.report-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 2;
}

.report-table th:first-child,
.report-table td:first-child,
.report-table th:nth-child(2),
.report-table td:nth-child(2),
.report-table th:nth-child(3),
.report-table td:nth-child(3) {
  text-align: left;
}

.report-table tr:nth-child(even) td {
  background: #0f141c;
}

.report-table tr:nth-child(odd) td {
  background: #0b0101;
}

.type-heading-row td {
  background: #343434 !important;
  color: #f5d76e;
  font-size: 18px;
  font-weight: 800;
  text-align: left !important;
  border-top: 2px solid #888;
}

.type-cell,
.item-cell {
  font-weight: bold;
  color: #e5e7eb;
}

.description-cell {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.total-qty-cell {
  font-weight: bold;
}

.total-value-cell {
  font-weight: bold;
  color: #f5d76e;
}

.total-row td {
  background: #1f2937 !important;
  font-weight: bold;
  border-top: 3px double #888;
}

.total-row td:first-child {
  text-align: left;
}

.empty-row {
  text-align: center !important;
  padding: 18px !important;
  opacity: 0.75;
}

.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.side-help {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px;
  background: rgb(67, 67, 67);
  font-size: 13px;
  line-height: 1.6;
  color: #cbbfbf;
}

@media (max-width: 900px) {
  .report-layout {
    grid-template-columns: 1fr;
  }

  .left-side-section {
    margin-top: 0;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .side-help {
    width: 100%;
  }
}
</style>